import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { initTheme } from './lib/theme';
import { initLocale } from './i18n';
import { registerServiceWorker } from './lib/pwa';
import { installGlobalErrorHooks } from './components/ErrorBoundary';
import { initSentry } from './lib/sentry';
import './styles/globals.css';

// Apply dark-mode class + Brazilian locale BEFORE React mounts so the very
// first painted frame is in the right theme and language. Prevents the
// flash-of-wrong-theme and the flash-of-English that would otherwise show
// for a tick on slow devices.
initTheme();
initLocale();

// Wave bbbbbb · 2026-05-16 — write the build stamp onto the root element
// BEFORE React mounts so ErrorBoundary + SettingsPage / Sobre can read it
// via document.documentElement.getAttribute('data-build-id'). The string
// is replaced by Vite at build time via `define` (see vite.config.ts).
try {
  if (typeof document !== 'undefined' && typeof __BUILD_STAMP__ === 'string') {
    document.documentElement.setAttribute('data-build-id', __BUILD_STAMP__);
    // Best-effort deploy timestamp — `__BUILD_STAMP__` already contains an
    // ISO-ish portion, but we also expose the raw build wall-clock for the
    // Sobre section's "Implantado em" row.
    const m = __BUILD_STAMP__.match(/(\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2})/);
    const matched = m?.[1];
    if (matched) {
      // restore the colons so it parses as ISO
      const iso = matched.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
      document.documentElement.setAttribute('data-deploy-time', iso);
    }
  }
} catch {
  // Setting an attribute on documentElement should never throw, but if
  // the global is missing in some exotic SSR/test environment we just
  // skip — Sobre falls back to 'dev'.
}

// Wave 310 — capture window.onerror + unhandledrejection into the
// /v1/client-errors pipeline. React error boundaries only see render
// crashes; async errors fall through to here. Idempotent.
installGlobalErrorHooks();

// Production launch · 2026-05-20 — opt-in Sentry frontend bridge.
// No-op quando VITE_SENTRY_DSN não está setado (dev / preview), então
// rebuilds não pagam custo. Quando configurado, ErrorBoundary +
// window.onerror enviam pra Sentry em paralelo ao /v1/client-errors.
try {
  initSentry({
    environment:
      (import.meta as unknown as { env?: { MODE?: string } }).env?.MODE ?? 'production',
    release: typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : undefined,
  });
} catch {
  // initSentry itself never throws today, but the guard makes the
  // bootstrap resilient to future signature changes.
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Register the service worker after mount. Deliberately fire-and-forget —
// we never want a failed SW register to block app startup. The worker is
// a progressive enhancement (shell cache + offline fallback); without it
// the app still works exactly as before, just without offline coverage.
registerServiceWorker();
